import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Rocket, Coins, FolderOpen, Zap, CalendarDays } from 'lucide-react';
import { useAuthStore } from 'store/authStore';
import { getDisplayName } from 'utils/userDisplay';
import { ScenarioStat } from 'types';
import { useAdminUsers } from 'hooks/useAdminUsers';
import { useDashboardStats } from 'hooks/useDashboardStats';
import KpiCard from 'components/dashboard/KpiCard';
import ActivityChart from 'components/dashboard/ActivityChart';
import ScenarioBreakdown from 'components/dashboard/ScenarioBreakdown';
import TokenUsage from 'components/dashboard/TokenUsage';
import EfficiencyTable from 'components/dashboard/EfficiencyTable';
import { avatarColor } from 'utils/avatarColor';

function fmtVal(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}М`;
  if (n >= 1000) return `${Math.round(n / 1000)}К`;
  return String(n);
}

function UserPickerButton({
  label,
  initials,
  seed,
  active,
  onClick,
}: {
  label: string;
  initials: string;
  seed: string;
  active: boolean;
  onClick: () => void;
}) {
  const color = initials ? avatarColor(seed) : '#8b95a8';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        bgcolor: active ? 'var(--color-surface)' : 'transparent',
        boxShadow: active ? 'var(--shadow-sm)' : 'none',
        border: active ? '1px solid var(--color-border)' : '1px solid transparent',
        color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
        fontWeight: active ? 600 : 400,
        transition: 'all 0.15s',
        '&:hover': {
          bgcolor: active ? 'var(--color-surface)' : 'rgba(255,255,255,0.6)',
        },
        userSelect: 'none',
      }}
    >
      {initials && (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            flexShrink: 0,
            letterSpacing: '0.02em',
          }}
        >
          {initials}
        </Box>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 'inherit', color: 'inherit', lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [selectedUserId, setSelectedUserId] = useState<'all' | string>('all');
  const { users: adminUsers } = useAdminUsers();

  // For non-admin: filter by current user; for admin "all": no filter; for admin specific: filter by id
  const filterUserId = isAdmin ? (selectedUserId === 'all' ? undefined : selectedUserId) : user?.id;

  const stats = useDashboardStats(filterUserId);

  // Map ScenarioStatReal → ScenarioStat (rename tokensEstimate → tokens)
  const scenarioStats: ScenarioStat[] = stats.scenarioStats.map((s) => ({
    scenarioId: s.scenarioId,
    title: s.title,
    requests: s.requests,
    tokens: s.tokensEstimate,
  }));

  // FeedItemReal already has color, so it's compatible with FeedItem
  // const feed: FeedItem[] = stats.feed as FeedItem[];

  const pageTitle = isAdmin
    ? selectedUserId === 'all'
      ? 'Все пользователи'
      : (adminUsers.find((u) => u.id === selectedUserId)?.name ?? 'Статистика')
    : 'Моя статистика';

  return (
    <Box sx={{ p: { xs: 2, md: '32px 48px' }, maxWidth: 1600, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: isAdmin ? 2 : 3.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px', mb: 0.5 }}>
          {pageTitle}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          Апрель 2026{!isAdmin && user?.name ? ` · ${user.name}` : ''}
        </Typography>
      </Box>

      {/* Admin user picker */}
      {isAdmin && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 3,
            p: 0.75,
            background: 'rgba(255,255,255,0.88)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid rgba(20,116,255,0.12)',
            boxShadow: '0 2px 12px rgba(20,116,255,0.08)',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(20,116,255,0.18)', borderRadius: 99 },
          }}
        >
          <UserPickerButton
            label="Все пользователи"
            initials=""
            seed=""
            active={selectedUserId === 'all'}
            onClick={() => setSelectedUserId('all')}
          />
          {adminUsers.map((u) => (
            <UserPickerButton
              key={u.id}
              label={getDisplayName(u.name, u.email)}
              initials={u.avatarInitials}
              seed={u.id}
              active={selectedUserId === u.id}
              onClick={() => setSelectedUserId(u.id)}
            />
          ))}
        </Box>
      )}

      {/* KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2.5, mb: 3 }}>
        <KpiCard
          label="Всего сессий"
          value={fmtVal(stats.totalSessions)}
          delta={`+${stats.todaySessions} сегодня`}
          deltaType={stats.todaySessions > 0 ? 'up' : 'neutral'}
          icon={<Rocket size={18} />}
          accentColor="#1474FF"
        />
        <KpiCard
          label="Токенов (оценка)"
          value={fmtVal(stats.tokensEstimate)}
          delta={`${Math.round((stats.tokensEstimate / stats.mockTokenLimit) * 100)}% лимита`}
          deltaType="neutral"
          icon={<Coins size={18} />}
          accentColor="#7C3AED"
        />
        <KpiCard
          label="Документов обработано"
          value={fmtVal(stats.filesProcessed)}
          delta={stats.todaySessions > 0 ? `+${stats.todaySessions} сегодня` : 'нет новых'}
          deltaType={stats.todaySessions > 0 ? 'up' : 'neutral'}
          icon={<FolderOpen size={18} />}
          accentColor="#0EA5E9"
        />
        <KpiCard
          label="Среднее время ответа"
          value={`${stats.avgResponseTimeSec} с`}
          delta="среднее"
          deltaType="up"
          icon={<Zap size={18} />}
          accentColor="#10B981"
        />
        <KpiCard
          label="Активных дней"
          value={fmtVal(stats.activeDays)}
          delta="этот месяц"
          deltaType="neutral"
          icon={<CalendarDays size={18} />}
          accentColor="#F59E0B"
        />
      </Box>

      {/* Charts row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 320px', gap: 2.5, mb: 3 }}>
        <ActivityChart data={stats.weekActivity} />
        <ScenarioBreakdown data={scenarioStats} />
        <TokenUsage
          used={stats.tokensEstimate}
          limit={stats.mockTokenLimit}
          history={stats.mockMonthTokens}
        />
      </Box>

      {/* Bottom row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
        {/* <ActivityFeed items={feed} /> */}
        <EfficiencyTable data={scenarioStats} />
      </Box>
    </Box>
  );
}
